import { useEffect, useState } from "react";

/**
 * "Hent som app" (v19). The browser decides whether it can install the site (Chrome, Edge and Android: a one-tap prompt we can open from our own
 * button; iPhone/iPad and Safari: the person adds it from the share menu – we only explain how). The event can fire before anything is on screen,
 * so it is captured at start-up (`initInstall()` in main.tsx) and kept here.
 */
let deferred: any = null;
let installed = false;
const subs = new Set<() => void>();
const tell = () => subs.forEach((f) => f());

export function initInstall() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; tell(); });
  window.addEventListener("appinstalled", () => { deferred = null; installed = true; tell(); });
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: window-controls-overlay)").matches || (navigator as any).standalone === true;
  } catch {
    return false;
  }
}

export type InstallHint = "ios" | "android" | "chromium" | "safari" | "firefox" | "other";
export function installHint(ua = typeof navigator === "undefined" ? "" : navigator.userAgent, touchPoints = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints, platform = typeof navigator === "undefined" ? "" : navigator.platform): InstallHint {
  if (/iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && touchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Edg\/|Chrome\/|Chromium\//.test(ua)) return "chromium";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

/** opens the browser's own install question – resolves to what the person answered, or "unavailable" when the browser has no prompt to open */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const e = deferred;
  if (!e) return "unavailable";
  deferred = null;
  tell();
  try {
    await e.prompt();
    const r = await e.userChoice;
    return r?.outcome === "accepted" ? "accepted" : "dismissed";
  } catch {
    return "unavailable";
  }
}

export function useInstall() {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return { standalone: installed || isStandalone(), canPrompt: !!deferred, hint: installHint() };
}
